// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { Suspense, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Header from "./header";
import Sidebar, { DrawerHeader } from "./sidebar";
import {
  Outlet,
  useLocation,
  useNavigate,
  matchRoutes,
} from "react-router-dom";
import { routes } from "../route";

import ConfirmationModalContextProvider from "@context/DialogContext";
import { selectDecodedIdToken, selectRoles } from "@slices/authSlice";
import { useSnackbar } from "notistack";
import pJson from "../../package.json";
import { RootState, useAppSelector } from "@slices/store";
import { Typography } from "@mui/material";
import { tokens } from "../theme";
import { LoadingEffect } from "@component/ui/Loading";
import { Loading } from "@config/constant";
import { Role } from "@utils/types";
import { hasRequiredRole } from "@utils/utils";

export default function Layout() {
  //snackbar configuration
  const { enqueueSnackbar } = useSnackbar();
  const common = useAppSelector((state: RootState) => state.common);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === "/") {
      navigate("/interview", { replace: true });
      return;
    }
    const feedbackId = sessionStorage.getItem("feedbackId");
    if (feedbackId) {
      const feedbackPath = `/interview/${feedbackId}/feedback`;

      if (location.pathname !== feedbackPath) {
        navigate(feedbackPath, { replace: true });
        sessionStorage.removeItem("feedbackId");
      }
    }
  }, [location.pathname, navigate]);

  const roles = useSelector(selectRoles);

  useEffect(() => {
    if (common.timestamp != null) {
      enqueueSnackbar(common.message, {
        variant: common.type,
        preventDuplicate: true,
        anchorOrigin: { horizontal: "right", vertical: "bottom" },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [common.timestamp]);

  useEffect(() => {
    const link = localStorage.getItem("internal-app-redirect-url");
    if (link && link !== "/") {
      const currentFullPath = location.pathname + location.search;
      if (currentFullPath !== link) {
        navigate(link, { replace: true });
      }
      localStorage.removeItem("internal-app-redirect-url");
    }
  }, []);
  // TODO : Commented this to avoid the re-direction to the specified Url

  // if (hasRequiredRole(roles, [Role.MARKETING_ROLE, Role.LEAD_ROLE])) {
  //   navigate("/");
  //   return;
  // }
  // if (hasRequiredRole(roles, [Role.MARKETING_ROLE])) {
  //   navigate("/vacancy");
  // }
  // }, []);

  const matches = matchRoutes(routes, location.pathname);
  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const decodedIdToken = useSelector(selectDecodedIdToken);

  const getAppBarTitle = (): string => {
    var title: string = "";
    matches?.forEach((obj) => {
      if (location.pathname === obj.pathname) {
        title = obj.route.text;
      }
    });

    return title;
  };

  return (
    <ConfirmationModalContextProvider>
      <Box sx={{ display: "flex", height: "100vh", minWidth: 0 }}>
        <CssBaseline />

        <Sidebar
          roles={roles}
          currentPath={location.pathname}
          open={open}
          handleDrawer={() => setOpen(!open)}
          theme={theme}
        />
        <Header
          theme={theme}
          title={getAppBarTitle()}
          open={open}
          email={decodedIdToken?.email}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <DrawerHeader open={open} />
          {/* tab content start from here */}
          <Suspense fallback={<LoadingEffect message={Loading} isFullscreen />}>
            <Outlet />
          </Suspense>
          <Box
            className="layout-note"
            sx={{
              background:
                theme.palette.mode === "light"
                  ? tokens(theme.palette.mode).customColors.bottomBarLight
                  : tokens(theme.palette.mode).customColors.bottomBarDark,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: tokens(theme.palette.mode).customColors.bottomBarText,
                ml: 3,
              }}
            >
              v {pJson.version} | © {new Date().getFullYear()} WSO2 LLC
            </Typography>
          </Box>
        </Box>
      </Box>
    </ConfirmationModalContextProvider>
  );
}
